import { CartPackage, Package } from "@prisma/client";
// import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@radix-ui/react-accordion";
import { Link, useActionData } from "@remix-run/react";

import { Button } from "~/components";
import { Form, FormError, useForm, z } from "~/components/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { CheckIcon, DeleteIcon, EditIcon, CandleIcon } from "~/icons";
import { cn, currency } from "~/lib/utils";
import * as Tier from "~/routes/_store+/_breadcrumbs+/packages+/$flow_.$packageId.$screen/Tier";
import { ServiceList, ServiceListItem } from "~/routes/_store+/_breadcrumbs+/packages+/ServiceList";

export const DELETE_CART_PACKAGE = "delete-cart-package";

export function PackageDisplay({ cartPackage, lastResult }: { cartPackage: CartPackage & { salePackage: Package } }) {
    const { salePackage, amounts, customerSelection, packageData } = cartPackage;

    // TODO we shouldn't have to define the field we care about
    // we should go through customerSelection, and grab services that have been configured, then grab the summary data needed for it.
    const container = packageData.find(({ field }) => "containers-tier" === field);
    const urn = packageData
        .find(({ field }) => "urn-allowance" === field)
        ?.items.find(({ value }) => value === customerSelection.urn);

    const services = packageData.filter(({ type, field }) => {
        return type !== "discount" && !["containers-tier", "urn-allowance"].includes(field);
    });

    const images = [];

    if (container && container.image) {
        images.push(container.image);
    }

    if (urn && urn.image) {
        images.push(urn.image);
    }

    if (customerSelection?.container) {
        const packageDataWithItems = packageData.find((item) => item.items);
        const container = packageDataWithItems.items?.find((item) => item.name === customerSelection.container);
        if (container?.image) {
            images.push(container?.image);
        }
    }

    return (
        <div className="flex flex-col md:flex-row items-start gap-8 w-full overflow-hidden">
            <div
                className={cn(
                    "flex-shrink-0 w-48 h-48 flex justify-center items-center p-2 border-4 border-gray-100",
                    images.length < 1 && "bg-gray-100",
                )}
            >
                {images.length > 0 ? (
                    images.map((image, index) => <img key={index} src={image} className="object-contain w-1/2 h-1/2" />)
                ) : (
                    <CandleIcon className="w-10 h-10" />
                )}
            </div>
            <div className="flex-1 flex flex-col gap-3">
                <section className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="text-2xl font-title">{salePackage.name}</h2>
                        <div className="text-lg">{currency(amounts.subtotal)}</div>
                    </div>
                </section>

                {customerSelection.packageSlug === "direct-cremation" ? (
                    <p>{customerSelection?.container}</p>
                ) : (
                    <>
                        <p className="opacity-60">Package details:</p>
                        <ul className="flex flex-col gap-2">
                            <CustomerSelectionItem
                                to={`/packages/cremations/${customerSelection.packageSlug}/package-tier`}
                                label="Package merchandise"
                                value={Tier.getValue(customerSelection)}
                            />
                        </ul>
                        <Accordion type="single" collapsible>
                            <AccordionItem value="item-1" className="border-0">
                                <AccordionTrigger className="py-0 justify-start gap-3 font-medium">
                                    <p className="">View more details</p>
                                </AccordionTrigger>
                                <AccordionContent className="pt-4">
                                    <ServiceList>
                                        {services.map((service, index: number) => (
                                            <ServiceListItem key={index}>{service.name}</ServiceListItem>
                                        ))}
                                    </ServiceList>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </>
                )}

                <DeletePackage cartPackageId={cartPackage.id} lastResult={lastResult} />
            </div>
        </div>
    );
}

function CustomerSelectionItem({ to, label, value, children }) {
    return (
        <li>
            <div className="flex flex-wrap md:grid auto-cols-max grid-flow-col gap-3">
                <div className="flex gap-2 items-center">
                    <CheckIcon className="text-secondary w-4 h-4" />
                    {label}:
                </div>
                <div className="font-medium">{value}</div>
                <Link to={to} className="flex items-center underline text-sm ml-2 gap-2">
                    <EditIcon width="1em" height="1em" /> Edit
                </Link>
            </div>
            {children}
        </li>
    );
}

const removePackageSchema = z.object({
    cartPackageId: z.string(),
    intent: z.literal(DELETE_CART_PACKAGE),
});

function DeletePackage({ cartPackageId }: { cartPackageId: string }) {
    const lastResult = useActionData();
    const [form] = useForm(removePackageSchema, {
        lastResult,
        defaultValue: {
            cartPackageId: "",
        },
    });

    return (
        <Form method="post" form={form}>
            <FormError />
            <input name="cartPackageId" value={cartPackageId} type="hidden" readOnly />
            <Button
                className="flex items-center justify-center gap-1 text-destructive text-sm capitalize"
                name="intent"
                value={DELETE_CART_PACKAGE}
                type="submit"
                variant="action"
            >
                <DeleteIcon className="w-4 h-4" />
                Remove
            </Button>
        </Form>
    );
}
